import { Eye, Trash2 } from 'lucide-react';
import { useContext, useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import wishlistImg from '../../assets/media/wishlist.png';
import CartContext from '../../contexts/CartContext';
import apiClient, { IMAGE_BASE_URL } from '../../utils/api-client';
import './MyWishList.css';
const MyWishlist = () => {
  const user = localStorage.getItem('userID');
  const [wishlistItems, setWishlistItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  useContext(CartContext);
  useEffect(() => {
    const fetchWishlistData = () => {
      if (!user) {
        if (!toast.isActive('login-toaster'))
          toast.info('Login to view wishlist.', {
            toastId: 'login-toaster',
          });
        navigate('/login');
        return;
      }
      setIsLoading(true);
      apiClient
        .post(`/get-wishlist`, { user_id: user })
        .then((res) => {
          if (res.data.status === true) {
            setWishlistItems(res.data.data);
            setIsLoading(false);
          }
        })
        .catch(() => {
          setIsLoading(false);
          toast.error('Failed to fetch wishlist items. Please try again.');
        });
    };
    fetchWishlistData();
  }, [user, navigate]);

  const handleRemoveFromWishlist = async (w_id) => {
    try {
      await apiClient.delete(`/wishlist-remove/${w_id}`);
      setWishlistItems(wishlistItems.filter((item) => item.w_id !== w_id));
      toast.success('Item removed from wishlist successfully!');
    } catch (error) {
      toast.error('Failed to remove item from wishlist. Please try again.');
    }
  };
  return (
    <section className="wishlist-section">
      <div className="card border-0 rounded-3">
        <div>
          {/* Empty State */}
          {wishlistItems.length === 0 && !isLoading ? (
            <div className="empty-state text-center py-3 ">
              <img
                className="img-fluid mb-3"
                style={{ opacity: 0.15 }}
                src={wishlistImg}
                alt="Wishlist Empty"
              />
              <p className="lead fw-semibold text-muted mb-3">
                Your wishlist is empty
              </p>
              <Link to="/shop/" className="btn btn-sm btn-purple fw-semibold">
                Explore Products
              </Link>
            </div>
          ) : (
            <>
              {/* Table View (Desktop) */}
              <div className="table-responsive d-none d-md-block">
                <table className="table align-middle table-hover">
                  <thead className="table-light">
                    <tr>
                      <th>Image</th>
                      <th>Name</th>
                      <th>Status</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr>
                        <td colSpan={4}>
                          <Skeleton count={1} height={40} />
                        </td>
                      </tr>
                    ) : (
                      wishlistItems.map((item, index) => (
                        <tr key={index} className="p-2">
                          <td className=" ">
                            <img
                              className="border rounded"
                              style={{
                                width: '40px',
                                height: '40px',
                                objectFit: 'cover',
                              }}
                              src={`${IMAGE_BASE_URL}/${item.products.product_image}`}
                              alt={item.products.alt_content}
                            />
                          </td>
                          <td
                            style={{
                              maxWidth:
                                window.innerWidth > 900 ? '300px' : '100%',
                            }}
                            className="fw-semibold text-grey text-truncate"
                          >
                            {item.products.product_name}
                          </td>
                          <td>
                            <span
                              className={`badge ${
                                item.products.in_stock > 0
                                  ? 'bg-success'
                                  : 'bg-danger'
                              }`}
                            >
                              {item.products.in_stock > 0
                                ? 'In Stock'
                                : 'Out of Stock'}
                            </span>
                          </td>
                          <td className="align-middle">
                            <div className="d-flex align-items-center justify-content-center gap-2">
                              <Link
                                to={`/shop/${item?.products?.subcategory?.s_cat_slug}/${item.products.product_slug}`}
                                className="btn btn-sm btn-purple"
                              >
                                <Eye size={16} className="me-1 mb-1" /> View
                              </Link>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() =>
                                  handleRemoveFromWishlist(item.w_id)
                                }
                              >
                                <Trash2 size={16} className="me-1 mb-1" />{' '}
                                Remove
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Card View (Mobile) */}
              <div className="d-block d-md-none">
                {isLoading
                  ? [...Array(3)].map((_, i) => (
                      <div key={i} className="mb-3">
                        <Skeleton height={120} />
                      </div>
                    ))
                  : wishlistItems.map((item, index) => (
                      <div
                        key={index}
                        className="wishlist-card border rounded-3 p-3 mb-3 shadow-sm"
                      >
                        <div className="d-flex">
                          <img
                            className="border rounded me-3"
                            style={{
                              width: '100px',
                              height: '100px',
                              objectFit: 'cover',
                            }}
                            src={`${IMAGE_BASE_URL}/${item.products.product_image}`}
                            alt={item.products.alt_content}
                          />
                          <div className="flex-grow-1">
                            <h6
                              style={{ maxWidth: '210px' }}
                              className="fw-semibold mb-2 text-truncate"
                            >
                              {item.products.product_name}
                            </h6>
                            <span
                              className={`badge ${
                                item.products.in_stock > 0
                                  ? 'bg-success'
                                  : 'bg-danger'
                              } mb-3`}
                            >
                              {item.products.in_stock > 0
                                ? 'In Stock'
                                : 'Out of Stock'}
                            </span>
                            <div className="d-flex align-items-center gap-2">
                              <Link
                                to={`/shop/${item?.products?.subcategory?.s_cat_slug}/${item.products.product_slug}`}
                                className="btn btn-sm btn-light d-flex align-items-center justify-content-center action-btn text-purple"
                                title="View Product"
                              >
                                <Eye size={16} />
                              </Link>

                              <button
                                onClick={() =>
                                  handleRemoveFromWishlist(item.w_id)
                                }
                                className="btn btn-sm btn-light d-flex align-items-center justify-content-center action-btn text-red"
                                title="Remove from Wishlist "
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};
export default MyWishlist;
